import { useForm } from 'react-hook-form';
import { cn } from '@/lib/utils.ts';
import {
  Project,
  ProjectSchema,
  ProjectCreateSchema,
  ProjectUpdateSchema,
  Subcategory,
  ContentFormat,
  createProject,
  updateProject,
  getGraph,
} from '@/api/api.ts';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useCallback, useEffect, useState } from 'react';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form.tsx';
import { Input } from '@/components/ui/input.tsx';
import { Textarea } from '@/components/ui/textarea.tsx';
import { TiptapEditor } from '@/components/ui/tiptap-editor.tsx';

export default function ProjectForm({
  project,
  onClose,
  isEditing = true,
}: {
  project: Project;
  onClose: () => void;
  isEditing?: boolean;
}) {
  const [subcategories, setSubcategories] = useState<Subcategory[]>([]);
  const [loadingSubcategories, setLoadingSubcategories] = useState(true);

  // Create schemas without dataLayers and relations for form validation
  const ProjectFormSchema = ProjectSchema.omit({
    dataLayers: true,
    relations: true,
  });

  // Create form type from the schema
  type ProjectFormData = z.infer<typeof ProjectFormSchema>;

  const form = useForm<ProjectFormData>({
    resolver: zodResolver(isEditing ? ProjectFormSchema : ProjectCreateSchema),
    defaultValues: {
      ...project,
      // Convert null values to empty strings to avoid React warnings
      shortNameDe: project.shortNameDe ?? '',
      nameDe: project.nameDe ?? '',
      contentDe: project.contentDe ?? '',
      shortContentDe: project.shortContentDe ?? '',
      doiLabel: project.doiLabel ?? '',
      doiLink: project.doiLink ?? '',
    },
  });

  // Load subcategories for the dropdown
  useEffect(() => {
    const loadSubcategories = async () => {
      try {
        const controller = new AbortController();
        const graph = await getGraph(controller.signal);
        const allSubcategories = graph.categories.flatMap(
          (cat) => cat.subcategories
        );
        setSubcategories(allSubcategories);
      } catch (error) {
        console.error('Failed to load subcategories:', error);
      } finally {
        setLoadingSubcategories(false);
      }
    };

    loadSubcategories();
  }, []);

  const onSubmit = useCallback(
    async (data: ProjectFormData) => {
      try {
        // Filter out empty author names
        const authorsArray = Array.isArray(data.authors)
          ? data.authors.filter((author: string) => author.trim() !== '')
          : [];

        // Helper function to convert empty strings to null for submission
        const emptyToNull = (value: string | null | undefined) =>
          value && value.trim() !== '' ? value : null;

        const projectData = {
          ...data,
          authors: authorsArray,
          // Convert empty strings to null for nullable fields
          shortNameDe: emptyToNull(data.shortNameDe),
          nameDe: emptyToNull(data.nameDe),
          contentDe: emptyToNull(data.contentDe),
          shortContentDe: emptyToNull(data.shortContentDe),
          doiLabel: emptyToNull(data.doiLabel),
          doiLink: emptyToNull(data.doiLink),
          // Preserve existing values for complex fields that aren't in the form
          relations: project.relations || {},
          dataLayers: project.dataLayers || [],
        };

        if (isEditing) {
          await updateProject(data.id, {
            subcategoryId: projectData.subcategoryId,
            shortName: projectData.shortName,
            shortNameDe: projectData.shortNameDe,
            name: projectData.name,
            nameDe: projectData.nameDe,
            authors: projectData.authors,
            content: projectData.content,
            contentDe: projectData.contentDe,
            shortContent: projectData.shortContent,
            shortContentDe: projectData.shortContentDe,
            contentFormat: projectData.contentFormat,
            doiLabel: projectData.doiLabel,
            doiLink: projectData.doiLink,
            relations: projectData.relations,
            dataLayers: projectData.dataLayers,
          });
        } else {
          await createProject(projectData);
        }
        form.reset();
        onClose();
      } catch (error) {
        alert(
          `Speichern fehlgeschlagen, bitte erneut versuchen: ${
            error instanceof Error ? error.message : 'Unknown error'
          }`
        );
      }
    },
    [form, onClose, isEditing]
  );

  return (
    <>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)}>
          <div className={cn('py-[8px]')}>
            <FormField
              control={form.control}
              name="id"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>ID</FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      placeholder={'Project ID'}
                      disabled={form.formState.isSubmitting || isEditing}
                    />
                  </FormControl>
                  <FormDescription>
                    {isEditing
                      ? 'ID cannot be changed when editing'
                      : 'Unique identifier for the project'}
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <div className={cn('py-[8px]')}>
            <FormField
              control={form.control}
              name="subcategoryId"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Subcategory</FormLabel>
                  <FormControl>
                    <select
                      {...field}
                      disabled={
                        form.formState.isSubmitting || loadingSubcategories
                      }
                      className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      <option value="">Select a subcategory...</option>
                      {subcategories.map((subcategory) => (
                        <option key={subcategory.id} value={subcategory.id}>
                          {subcategory.label}
                        </option>
                      ))}
                    </select>
                  </FormControl>
                  <FormDescription>
                    {loadingSubcategories
                      ? 'Loading subcategories...'
                      : 'Select the parent subcategory'}
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <div className={cn('block', 'lg:flex', 'gap-x-[24px]')}>
            <div className={cn('flex-1', 'py-[8px]')}>
              <FormField
                control={form.control}
                name="shortName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Short Name</FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        placeholder={'Short Name'}
                        disabled={form.formState.isSubmitting}
                        icon={<span>🇺🇸</span>}
                      />
                    </FormControl>
                    <FormDescription />
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            <div className={cn('flex-1', 'py-[8px]')}>
              <FormField
                control={form.control}
                name="shortNameDe"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Short Name</FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        value={field.value ?? ''}
                        placeholder={'Short Name'}
                        disabled={form.formState.isSubmitting}
                        icon={<span>🇩🇪</span>}
                      />
                    </FormControl>
                    <FormDescription />
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          </div>

          <div className={cn('block', 'lg:flex', 'gap-x-[24px]')}>
            <div className={cn('flex-1', 'py-[8px]')}>
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Name</FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        placeholder={'Name'}
                        disabled={form.formState.isSubmitting}
                        icon={<span>🇺🇸</span>}
                      />
                    </FormControl>
                    <FormDescription />
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            <div className={cn('flex-1', 'py-[8px]')}>
              <FormField
                control={form.control}
                name="nameDe"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Name</FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        value={field.value ?? ''}
                        placeholder={'Name'}
                        disabled={form.formState.isSubmitting}
                        icon={<span>🇩🇪</span>}
                      />
                    </FormControl>
                    <FormDescription />
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          </div>

          <div className={cn('py-[8px]')}>
            <FormField
              control={form.control}
              name="authors"
              render={({ field }) => {
                const authors = Array.isArray(field.value) ? field.value : [];

                const addAuthor = () => {
                  field.onChange([...authors, '']);
                };

                const removeAuthor = (index: number) => {
                  const newAuthors = authors.filter((_, i) => i !== index);
                  field.onChange(newAuthors);
                };

                const updateAuthor = (index: number, value: string) => {
                  const newAuthors = [...authors];
                  newAuthors[index] = value;
                  field.onChange(newAuthors);
                };

                return (
                  <FormItem>
                    <FormLabel>Authors</FormLabel>
                    <div className="space-y-2">
                      {authors.map((author, index) => (
                        <div key={index} className="flex gap-2 items-center">
                          <Input
                            value={author}
                            onChange={(e) =>
                              updateAuthor(index, e.target.value)
                            }
                            placeholder={`Author ${index + 1}`}
                            disabled={form.formState.isSubmitting}
                            className="flex-1"
                          />
                          <button
                            type="button"
                            onClick={() => removeAuthor(index)}
                            disabled={form.formState.isSubmitting}
                            className="px-3 py-2 text-sm bg-red-500 text-white rounded hover:bg-red-600 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            Remove
                          </button>
                        </div>
                      ))}
                      {authors.length === 0 && (
                        <div className="text-gray-500 text-sm">
                          No authors added yet. Click "Add Author" to add the
                          first author.
                        </div>
                      )}
                      <button
                        type="button"
                        onClick={addAuthor}
                        disabled={form.formState.isSubmitting}
                        className="px-4 py-2 text-sm bg-blue-500 text-white rounded hover:bg-blue-600 disabled:opacity-50"
                      >
                        + Add Author
                      </button>
                    </div>
                    <FormDescription>Add individual authors</FormDescription>
                    <FormMessage />
                  </FormItem>
                );
              }}
            />
          </div>

          <div className={cn('py-[8px]')}>
            <FormField
              control={form.control}
              name="contentFormat"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Content Format</FormLabel>
                  <FormControl>
                    <select
                      {...field}
                      disabled={form.formState.isSubmitting}
                      className="flex h-10 w-full rounded-md border border-input bg-white px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      {Object.values(ContentFormat).map((format) => (
                        <option key={format} value={format}>
                          {format.charAt(0).toUpperCase() + format.slice(1)}
                        </option>
                      ))}
                    </select>
                  </FormControl>
                  <FormDescription>
                    Please be aware, that changing the content format after
                    creation will require to fix all content fields, including
                    content of data layers e.g. in story layers.
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <div className={cn('block', 'lg:flex', 'gap-x-[24px]')}>
            <div className={cn('flex-1', 'py-[8px]')}>
              <FormField
                control={form.control}
                name="content"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Content</FormLabel>
                    <FormControl>
                      <>
                        {form.watch('contentFormat') === ContentFormat.Text && (
                          <div className="relative">
                            <textarea
                              value={field.value}
                              onChange={(e) => {
                                field.onChange(e.target.value || null);
                              }}
                              placeholder={''}
                              disabled={form.formState.isSubmitting}
                              className={cn(
                                'flex min-h-[80px] w-full rounded-md border border-input bg-white px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50'
                              )}
                            />
                          </div>
                        )}
                        {form.watch('contentFormat') === ContentFormat.Html && (
                          <TiptapEditor
                            value={field.value ?? ''}
                            onChange={field.onChange}
                            placeholder={'Content'}
                            disabled={form.formState.isSubmitting}
                            icon={<span>🇺🇸</span>}
                          />
                        )}
                      </>
                    </FormControl>
                    <FormDescription />
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            <div className={cn('flex-1', 'py-[8px]')}>
              <FormField
                control={form.control}
                name="contentDe"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Content</FormLabel>
                    <FormControl>
                      <>
                        {form.watch('contentFormat') === ContentFormat.Text && (
                          <div className="relative">
                            <textarea
                              value={field.value ?? ''}
                              onChange={(e) => {
                                field.onChange(e.target.value || null);
                              }}
                              placeholder={''}
                              disabled={form.formState.isSubmitting}
                              className={cn(
                                'flex min-h-[80px] w-full rounded-md border border-input bg-white px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50'
                              )}
                            />
                          </div>
                        )}
                        {form.watch('contentFormat') === ContentFormat.Html && (
                          <TiptapEditor
                            value={field.value ?? ''}
                            onChange={field.onChange}
                            placeholder={'Content'}
                            disabled={form.formState.isSubmitting}
                            icon={<span>🇩🇪</span>}
                          />
                        )}
                      </>
                    </FormControl>
                    <FormDescription />
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          </div>

          <div className={cn('block', 'lg:flex', 'gap-x-[24px]')}>
            <div className={cn('flex-1', 'py-[8px]')}>
              <FormField
                control={form.control}
                name="shortContent"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Short Content</FormLabel>
                    <FormControl>
                      <>
                        {form.watch('contentFormat') === ContentFormat.Text && (
                          <div className="relative">
                            <textarea
                              value={field.value ?? ''}
                              onChange={(e) => {
                                field.onChange(e.target.value || null);
                              }}
                              placeholder={''}
                              disabled={form.formState.isSubmitting}
                              className={cn(
                                'flex min-h-[80px] w-full rounded-md border border-input bg-white px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50'
                              )}
                            />
                          </div>
                        )}
                        {form.watch('contentFormat') === ContentFormat.Html && (
                          <TiptapEditor
                            value={field.value ?? ''}
                            onChange={field.onChange}
                            placeholder={'Short Content'}
                            disabled={form.formState.isSubmitting}
                            icon={<span>🇺🇸</span>}
                          />
                        )}
                      </>
                    </FormControl>
                    <FormDescription />
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            <div className={cn('flex-1', 'py-[8px]')}>
              <FormField
                control={form.control}
                name="shortContentDe"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Short Content</FormLabel>
                    <FormControl>
                      <>
                        {form.watch('contentFormat') === ContentFormat.Text && (
                          <div className="relative">
                            <textarea
                              value={field.value ?? ''}
                              onChange={(e) => {
                                field.onChange(e.target.value || null);
                              }}
                              placeholder={''}
                              disabled={form.formState.isSubmitting}
                              className={cn(
                                'flex min-h-[80px] w-full rounded-md border border-input bg-white px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50'
                              )}
                            />
                          </div>
                        )}
                        {form.watch('contentFormat') === ContentFormat.Html && (
                          <TiptapEditor
                            value={field.value ?? ''}
                            onChange={field.onChange}
                            placeholder={'Short Content'}
                            disabled={form.formState.isSubmitting}
                            icon={<span>🇩🇪</span>}
                          />
                        )}
                      </>
                    </FormControl>
                    <FormDescription />
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          </div>

          <div className={cn('block', 'lg:flex', 'gap-x-[24px]')}>
            <div className={cn('flex-1', 'py-[8px]')}>
              <FormField
                control={form.control}
                name="doiLabel"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>DOI Label</FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        value={field.value ?? ''}
                        placeholder={'DOI Label'}
                        disabled={form.formState.isSubmitting}
                      />
                    </FormControl>
                    <FormDescription />
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            <div className={cn('flex-1', 'py-[8px]')}>
              <FormField
                control={form.control}
                name="doiLink"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>DOI Link</FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        value={field.value ?? ''}
                        placeholder={'https://doi.org/...'}
                        disabled={form.formState.isSubmitting}
                      />
                    </FormControl>
                    <FormDescription />
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          </div>

          <div className={cn('py-[8px]')}>
            <button
              type="submit"
              disabled={form.formState.isSubmitting}
              className="inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 bg-primary text-primary-foreground hover:bg-primary/90 h-10 px-4 py-2"
            >
              {form.formState.isSubmitting
                ? 'Saving...'
                : isEditing
                  ? 'Update Project'
                  : 'Create Project'}
            </button>
          </div>
        </form>
      </Form>
    </>
  );
}
